import { useProfilesData } from '@/features/user';
import type { ProfilesResponse } from '@/features/user';

export type ScheduledPaymentsStatus = {
  /** Whether any course has payment pending */
  isPaymentPending: boolean;
  /** Payment URL if payment is pending */
  paymentUrl: string | null;
  /** Course name if payment is pending */
  courseName: string | null;
  /**
   * Full payment metadata of the course driving the banner. Carries the
   * new-contract fields (grouped_schedule_status, amounts, offer) when the
   * backend sends them; null when no payment is pending.
   */
  metadata: ProfilesResponse.EnrolledCourseMetadata | null;
  isLoading: boolean;
};

/**
 * Payment status across ALL of the selected profile's courses that are
 * enrolled for scheduled payments (skipping trial courses).
 *
 * Universal — not gated to the selected course. Drives the global payment
 * banner and the sessions placeholder. Any course with a payment due
 * surfaces a banner; `metadata.grouped_schedule_status` only decides which
 * variant renders (CREATED + amounts → the offer/expired states, everything
 * else → the legacy banner).
 */
export const useScheduledPaymentsStatus = (): ScheduledPaymentsStatus => {
  const { enrolledCourses, selectedCourse } = useProfilesData();

  // `enrolledCourses` already excludes courses with status === 'converted'
  // (we never render those), so the payment banner won't surface them either.
  const allCourses = enrolledCourses ?? [];

  // Check the selected course first so the banner agrees with the hero CTA;
  // only fall back to other courses if the selected one is settled.
  const courses = [
    ...allCourses.filter(c => c.variant_id === selectedCourse?.variant_id),
    ...allCourses.filter(c => c.variant_id !== selectedCourse?.variant_id),
  ];

  const courseWithPaymentDue = courses.find(
    course => course.metadata?.is_payment_due === true
  );

  return {
    isPaymentPending: !!courseWithPaymentDue,
    paymentUrl: courseWithPaymentDue?.metadata?.bhanzu_payment_url ?? null,
    courseName: courseWithPaymentDue?.course_name ?? null,
    metadata: courseWithPaymentDue?.metadata ?? null,
    isLoading: false, // Payment data now comes from profiles, which has its own loading state
  };
};
